import { Box, Text, Select, Flex } from '@chakra-ui/react';
import useChartTheme from '../useChartTheme';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getDailyTransactions, DailyTransaction } from '../../../api/AdminAPI';

const DailyTransactionsChart = () => {
  const [data, setData] = useState<DailyTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<number>(7);
  
  const token = useSelector((state: RootState) => state.userData.apiKey);

  const {
    series: [series],
    grid,
    tick,
    tooltip,
    legend,
  } = useChartTheme(['chart.1']);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      
      setIsLoading(true);
      setError(null);
      
      const response = await getDailyTransactions(token, period, 0);
      
      if (response.success && response.data) {
        // Sort data by date (oldest to newest)
        const sortedData = [...response.data].sort((a, b) => 
          new Date(a.date).getTime() - new Date(b.date).getTime()
        );
        setData(sortedData);
      } else {
        setError(response.error || 'Failed to load data');
      }
      
      setIsLoading(false);
    };

    fetchData();
  }, [token, period]);

  if (error) {
    return (
      <Box p={4} borderRadius="md" bg="status.errorSubtle" color="status.error">
        <Text>Error loading data: {error}</Text>
      </Box>
    );
  }

  return (
    <Box className="chart-container" p={6} bg="bg.surface" borderRadius="lg" boxShadow="sm" border="1px solid" borderColor="border.subtle">
      <Flex justify="space-between" align="center" mb={4} flexDir={{ base: 'column', sm: 'row' }} gap={{ base: 2, sm: 0 }}>
        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold">
          📈 Daily Transactions
        </Text>
        <Select
          value={period}
          onChange={(e) => setPeriod(Number(e.target.value))}
          width={{ base: '100%', sm: '150px' }}
          size="sm"
        >
          <option value={7}>7 days</option>
          <option value={14}>14 days</option>
          <option value={30}>30 days</option>
        </Select>
      </Flex>

      {isLoading ? (
        <Flex justify="center" align="center" height="300px">
          <Text color="text.secondary">Loading data...</Text>
        </Flex>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" {...grid} />
            <XAxis 
              dataKey="date" 
              tick={tick}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getDate()}/${date.getMonth() + 1}`;
              }}
            />
            <YAxis tick={tick} />
            <Tooltip {...tooltip} />
            <Legend {...legend} />
            <Line
              type="monotone"
              dataKey="txn"
              name="Transactions"
              stroke={series}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
};

export default DailyTransactionsChart;

